import styled, { css, keyframes } from "styled-components";
import { Link } from "react-router-dom";

const rise = keyframes`
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// The claim is the one moment worth marking: the token lands, once.
const land = keyframes`
  0% { transform: scale(0.92); }
  60% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

export const Card = styled.div`
  &,
  & *,
  & *::before,
  & *::after {
    box-sizing: border-box;
  }

  position: relative;
  width: min(600px, 92vw);
  max-height: 88vh;
  overflow-y: auto;
  padding: 30px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.darkerNed};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  color: ${({ theme }) => theme.colors.ice};
  animation: ${rise} 0.22s cubic-bezier(0.16, 0.84, 0.34, 1) both;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.chalkAlpha40} transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.chalkAlpha20};
  }

  ::selection {
    background: ${({ theme }) => theme.colors.envyAlpha40};
    color: ${({ theme }) => theme.colors.ice};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 480px) {
    padding: 22px 20px;
  }
`;

export const Title = styled.h2`
  margin: 0 0 8px;
  font-family: Barlow, sans-serif;
  font-size: 26px;
  font-weight: 700;
  line-height: 32px;
  text-wrap: balance;
  max-width: 30ch;
  padding-right: 36px;
`;

export const Sub = styled.p`
  margin: 0 0 22px;
  max-width: 46ch;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 15px;
  line-height: 22px;
`;

export const Meter = styled.div`
  display: grid;
  gap: 8px;
  margin-bottom: 24px;
`;

export const MeterBar = styled.div`
  height: 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  overflow: hidden;
`;

export const MeterFill = styled.div<{ $pct: number }>`
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  height: 100%;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.alert};
`;

export const MeterRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 18px;
  text-transform: uppercase;

  strong {
    font-family: Barlow, sans-serif;
    font-style: italic;
    font-weight: 900;
    font-size: 17px;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    text-transform: none;
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const Daily = styled.section`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px 16px;
  padding: 18px;
  margin-bottom: 26px;
  border-radius: 10px;
  background: linear-gradient(
      ${({ theme }) => theme.colors.envyAlpha05} 0%,
      transparent 100%
    ),
    ${({ theme }) => theme.colors.navy300};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};

  h3 {
    margin: 0;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 8px;
    /* Breaks the longer state evenly instead of stranding the last word. */
    text-wrap: balance;
    font-family: Barlow, sans-serif;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
  }

  p {
    grid-column: 2;
    margin: 2px 0 0;
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 14px;
    line-height: 20px;
  }

  @media (max-width: 480px) {
    row-gap: 12px;
    align-items: start;

    p {
      grid-column: 1 / -1;
    }
  }
`;

export const DailyToken = styled.span<{ $claimed?: boolean }>`
  grid-row: span 2;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.colors.envyAlpha20};
  color: ${({ theme }) => theme.colors.envy};
  animation: ${({ $claimed }) => ($claimed ? land : "none")} 0.42s
    cubic-bezier(0.16, 0.84, 0.34, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 480px) {
    grid-row: 1;
  }
`;

export const Streak = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.envyAlpha20};
  color: ${({ theme }) => theme.colors.envy};
  font-family: Barlow, sans-serif;
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
`;

export const SectionLabel = styled.h3`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

export const Rows = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 10px;
`;

export const Row = styled.li`
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px 16px;
  padding: 16px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.navy600};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};

  h4 {
    margin: 0;
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-family: Barlow, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  p {
    grid-column: 2;
    margin: 2px 0 0;
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 14px;
    line-height: 20px;
  }

  @media (max-width: 480px) {
    row-gap: 12px;
    align-items: start;

    p {
      grid-column: 1 / -1;
    }
  }
`;

export const Tile = styled.span<{ $brand: string }>`
  grid-row: span 2;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: ${({ $brand }) => $brand};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha40};
  color: ${({ theme }) => theme.colors.ice};

  @media (max-width: 480px) {
    grid-row: 1;
  }
`;

export const Reward = styled.span`
  font-family: Barlow, sans-serif;
  font-style: italic;
  font-weight: 900;
  font-size: 18px;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.envy};
  white-space: nowrap;

  small {
    margin-left: 2px;
    font-family: Barlow, sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.chalk};
  }
`;

const control = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 999px;
  min-height: 42px;
  padding: 0 20px;
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 0.15s ease-out,
    border-color 0.15s ease-out;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    grid-row: auto;
    grid-column: 1 / -1;
    width: 100%;
  }
`;

export const Claim = styled.button`
  ${control}
  grid-row: span 2;
  min-width: 150px;
  background: ${({ theme }) => theme.colors.envyAlpha20};
  border: 1px solid ${({ theme }) => theme.colors.envy};
  color: ${({ theme }) => theme.colors.ice};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.envyAlpha40};
  }

  &:disabled {
    background: transparent;
    border-color: ${({ theme }) => theme.colors.chalkAlpha20};
    color: ${({ theme }) => theme.colors.chalk};
    cursor: default;
    font-variant-numeric: tabular-nums;
  }
`;

const secondary = css`
  grid-row: span 2;
  min-width: 124px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  color: ${({ theme }) => theme.colors.ice};

  &:hover {
    border-color: ${({ theme }) => theme.colors.chalkAlpha40};
    background: ${({ theme }) => theme.colors.chalkAlpha10};
  }
`;

export const Action = styled.button`
  ${control}
  ${secondary}
`;

export const ActionLink = styled(Link)`
  ${control}
  ${secondary}
`;

export const Foot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
`;

export const AllRewards = styled(Link)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

export const Upgrade = styled.button`
  ${control}
  flex: 1;
  min-width: 250px;
  background: ${({ theme }) => theme.colors.tierPlat};
  border: 1px solid ${({ theme }) => theme.colors.tierPlat};
  color: ${({ theme }) => theme.colors.ice};

  &:hover {
    background: transparent;
  }
`;
